/**
 * Session state, held deliberately in memory.
 *
 * The access token is a 15-minute EdDSA JWT and lives **only in a module
 * variable** — never in `localStorage`, where any XSS would lift it. Continuity
 * across a reload comes from the refresh cookie the server sets
 * (`HttpOnly; Secure; SameSite=Strict; Path=/api/v1/auth`, architecture spec
 * §13.1), which JavaScript cannot read and therefore cannot leak.
 *
 * The one thing that is persisted is a boolean: "this browser had a session".
 * Without it the portal would attempt a silent refresh on every cold visit by a
 * signed-out visitor, and — against a mock that answers every refresh with 200 —
 * would appear to log them in.
 */

import type { CurrentPrincipal } from '@deca/api'

const SESSION_FLAG = 'deca.session'

let accessToken: string | null = null
let expiresAtMs = 0

type Listener = () => void
const listeners = new Set<Listener>()

export interface SessionSnapshot {
  token: string | null
  principal: CurrentPrincipal | null
}

let principal: CurrentPrincipal | null = null

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getPrincipal(): CurrentPrincipal | null {
  return principal
}

export function setSession(token: string, expiresInSeconds: number, who: CurrentPrincipal): void {
  accessToken = token
  expiresAtMs = Date.now() + expiresInSeconds * 1000
  principal = who
  localStorage.setItem(SESSION_FLAG, '1')
  emit()
}

export function setPrincipal(who: CurrentPrincipal | null): void {
  principal = who
  emit()
}

export function clearSession(): void {
  accessToken = null
  expiresAtMs = 0
  principal = null
  localStorage.removeItem(SESSION_FLAG)
  emit()
}

/** Did this browser hold a session? Drives whether to try a silent refresh. */
export function hadSession(): boolean {
  return localStorage.getItem(SESSION_FLAG) === '1'
}

/** Refresh a minute early, so a request never goes out on a token about to die. */
export function accessTokenExpiringSoon(now = Date.now()): boolean {
  return accessToken != null && expiresAtMs - now < 60_000
}
